import { useFormContext } from 'react-hook-form';
import {
  MAX_AMOUNT_DIGITS,
  MAX_EMAIL_CHARS,
  MAX_NAME_CHARS,
  SEND_DATE_DISPLAY_FORMAT,
} from './constants';

import CurrencySelect from './currency-select';
import { ControlledTextField } from 'components/TextField/ControlledTextField';
import ControlledCheckBox from 'components/CheckBox/ControlledCheckBox';
import ControlledRadioButton from 'components/RadioButton/ControlledRadioButton';
import ControlledDateSelector from 'components/DateSelector/ControlledDateSelector';
import Button from 'components/Button/BaseButton';

import { Body } from 'components/text';

import type { FormProps, VoucherFieldKey, VoucherFieldName } from './types';
import type { MultiVoucherFormSchema } from './schema';

import styles from './Form.module.scss';
import { useVoucherContext } from '../context';
import AmountContainer from './amount-container';
import { getInputName } from './utils';

const Form: React.FC<FormProps> = ({ index, isLast }) => {
  const { watch, setValue } = useFormContext<MultiVoucherFormSchema>();

  const {
    state: { currencies },
  } = useVoucherContext();

  const today = new Date();

  const shouldSendLater =
    watch(getInputName(index, 'send_date_option')) === 'later';
  const shouldSelfSend = watch(getInputName(index, 'self_send')) === true;
  const selectedCurrencyCode = watch(getInputName(index, 'currency')) || 'GBP';

  const selectedCurrency = currencies.find(
    currency => currency.code === selectedCurrencyCode
  );
  const currencySymbol = selectedCurrency?.symbol || '';

  const selfSendMiddleware = (checked: boolean) => {
    setValue(
      getInputName(index, 'recipient_email'),
      checked ? watch(getInputName(index, 'creator_email')) : ''
    );
  };

  return (
    <div className={styles.container}>
      <div>
        <Body weight="demiBold" className={styles.amountTitle}>
          Choose a gift card amount between {currencySymbol}50 and{' '}
          {currencySymbol}1,500
        </Body>
        <AmountContainer
          name={getInputName(index, 'amount')}
          currencySymbol={currencySymbol}
          maxChars={MAX_AMOUNT_DIGITS}
        />
      </div>

      <div>
        <ControlledTextField
          maxChars={MAX_NAME_CHARS}
          name={getInputName(index, 'creator_name')}
          label="Sender name"
        />
        <ControlledTextField
          maxChars={MAX_EMAIL_CHARS}
          name={getInputName(index, 'creator_email')}
          label="Sender email"
        />
        <ControlledCheckBox
          name={getInputName(index, 'self_send')}
          checked={false}
          label="Send the e-gift card only to me"
          middleware={selfSendMiddleware}
        />
        {!shouldSelfSend && (
          <ControlledTextField
            maxChars={MAX_EMAIL_CHARS}
            name={getInputName(index, 'recipient_email')}
            label="Recipient email"
          />
        )}
        <ControlledTextField
          maxChars={MAX_NAME_CHARS}
          name={getInputName(index, 'recipient_name')}
          label="Recipient name (optional)"
        />
        <div className={styles.sendDateOption}>
          <ControlledRadioButton
            name={getInputName(index, 'send_date_option')}
            options={[
              {
                label: 'Send now',
                value: 'now',
              },
              {
                label: 'Send later',
                value: 'later',
              },
            ]}
          />
        </div>

        {shouldSendLater && (
          <div className={styles.sendDate}>
            <ControlledDateSelector
              name={getInputName(index, 'send_date')}
              label="Send date"
              minDate={today.toString()}
              dateFormat={SEND_DATE_DISPLAY_FORMAT}
            />
          </div>
        )}

        <ControlledTextField
          name={getInputName(index, 'message')}
          label="Your message"
          multiline
          minRows={5}
        />
        {isLast && (
          <>
            <Button size="full">Continue</Button>
            <div className={styles.currencySelect}>
              <CurrencySelect name={getInputName(index, 'currency')} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Form;
